import {
  Activity,
  BarChart3,
  Bell,
  Bot,
  BriefcaseBusiness,
  Building2,
  ChevronLeft,
  ClipboardCheck,
  FileStack,
  FolderKanban,
  HardDrive,
  Home,
  LayoutDashboard,
  Menu,
  Palette,
  Search,
  ShieldCheck,
  Sparkles,
  UserCheck,
  Users,
  Workflow,
  X,
  type LucideIcon,
} from "lucide-react";
import type { ReactNode } from "react";
import { useEffect, useMemo, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { getRouteOwnershipContext } from "../../lib/routeOwnershipRegistry";

type BusinessCenterOSAdminShellProps = {
  title: string;
  description: string;
  eyebrow?: string;
  children: ReactNode;
  actions?: ReactNode;
  statusItems?: Array<{ label: string; value: string | number }>;
};

type ShellNavItem = {
  label: string;
  to: string;
  icon: LucideIcon;
  description: string;
};

const navGroups: Array<{ title: string; items: ShellNavItem[] }> = [
  {
    title: "Command Center",
    items: [
      { label: "Dashboard", to: "/admin/business-center-os", icon: LayoutDashboard, description: "Business Center OS home" },
      { label: "Priorities", to: "/admin/business-center-os#today-priorities", icon: ClipboardCheck, description: "Today's operational queue" },
      { label: "Activity", to: "/admin/activity-log", icon: Activity, description: "Platform activity log" },
      { label: "Intelligence", to: "/admin/executive-dashboard", icon: BarChart3, description: "Executive intelligence" },
    ],
  },
  {
    title: "Client Operations",
    items: [
      { label: "Leads", to: "/admin/leads", icon: Workflow, description: "Lead intake and follow-up" },
      { label: "Clients", to: "/admin/truevance-client-center", icon: Users, description: "Shared client records" },
      { label: "Client Journey", to: "/admin/journey-dashboard", icon: UserCheck, description: "Journey and conversion visibility" },
      { label: "Projects", to: "/admin/truevance-project-center", icon: FolderKanban, description: "Delivery and milestones" },
    ],
  },
  {
    title: "Workflow Operations",
    items: [
      { label: "Workflows", to: "/admin/automation-engine", icon: Workflow, description: "Automation and workflow rules" },
      { label: "Work Queue", to: "/admin/truevance-tasks", icon: ClipboardCheck, description: "Universal reminders and follow-ups" },
      { label: "Business Notes", to: "/admin/business-notes", icon: Activity, description: "Universal relationship and delivery notes" },
      { label: "Registry Inspector", to: "/admin/operational-registry", icon: Search, description: "Read-only operational registry diagnostics" },
      { label: "Documents", to: "/admin/truevance-document-center", icon: FileStack, description: "Client and project files" },
      { label: "Approvals", to: "/admin/agreement-records", icon: ShieldCheck, description: "Agreements and review states" },
    ],
  },
  {
    title: "Intelligence & Automation",
    items: [
      { label: "Intelligence", to: "/admin/executive-dashboard", icon: BarChart3, description: "Platform health and reporting" },
      { label: "Automation Engine", to: "/admin/automation-engine", icon: Workflow, description: "Operational automation" },
      { label: "AI Recommendations", to: "/admin/truevance-recommendations", icon: Bot, description: "Human-reviewed guidance" },
      { label: "Reports", to: "/admin/truevance-analytics-center", icon: Activity, description: "Analytics and reporting" },
    ],
  },
  {
    title: "Platform Services",
    items: [
      { label: "Memory Media Vault", to: "/admin/memory-hub/vault", icon: HardDrive, description: "Media review and archive" },
      { label: "Client Portal", to: "/client/portal", icon: Home, description: "Client-facing portal preview" },
      { label: "Workspace Provisioning", to: "/admin/workspace-provisioning-center", icon: Building2, description: "Workspace setup" },
      { label: "White Label Center", to: "/admin/white-label-branding-center", icon: Palette, description: "Branding and portal identity" },
    ],
  },
  {
    title: "Industry Workspaces",
    items: [
      { label: "CollabRecover AR Solutions", to: "/admin/ar-recovery", icon: ShieldCheck, description: "Recovery operations" },
      { label: "Truevance Connect", to: "/admin/truevance-business-center", icon: BriefcaseBusiness, description: "Consulting workspace" },
      { label: "Kreatethedate", to: "/admin/forms-directory", icon: Sparkles, description: "Event experience workspace" },
    ],
  },
];

export function BusinessCenterOSAdminShell({
  title,
  description,
  eyebrow = "Business Center OS",
  children,
  actions,
  statusItems = [],
}: BusinessCenterOSAdminShellProps) {
  const location = useLocation();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const routeContext = getRouteOwnershipContext(location.pathname);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname, location.hash]);

  const contextLine = useMemo(() => {
    const parts = [routeContext.platformName];
    if (routeContext.workspaceName) parts.push(routeContext.workspaceName);
    parts.push(routeContext.moduleName);
    return parts.join(" / ");
  }, [routeContext.moduleName, routeContext.platformName, routeContext.workspaceName]);

  return (
    <div className="min-h-screen overflow-x-hidden bg-[#07111F] text-white">
      <a
        href="#business-center-os-content"
        className="focus-ring sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[90] focus:rounded-lg focus:bg-cyan-200 focus:px-4 focus:py-3 focus:text-sm focus:font-black focus:text-slate-950"
      >
        Skip to content
      </a>

      <aside
        className={`fixed inset-y-0 left-0 z-40 hidden border-r border-white/10 bg-[#07111F]/96 text-white shadow-2xl shadow-black/30 backdrop-blur-xl transition-[width] duration-200 xl:flex xl:flex-col ${
          sidebarCollapsed ? "w-24" : "w-80"
        }`}
        aria-label="Business Center OS navigation"
      >
        <SidebarContent collapsed={sidebarCollapsed} onCollapseToggle={() => setSidebarCollapsed((current) => !current)} />
      </aside>

      {mobileOpen ? (
        <div className="fixed inset-0 z-50 xl:hidden">
          <button
            type="button"
            className="absolute inset-0 bg-slate-950/70"
            aria-label="Close navigation"
            onClick={() => setMobileOpen(false)}
          />
          <aside className="relative flex h-full w-[min(88vw,22rem)] flex-col border-r border-white/10 bg-[#07111F] shadow-2xl">
            <SidebarContent collapsed={false} onCollapseToggle={() => setMobileOpen(false)} mobile />
          </aside>
        </div>
      ) : null}

      <div className={`min-h-screen transition-[padding] duration-200 ${sidebarCollapsed ? "xl:pl-24" : "xl:pl-80"}`}>
        <header className="sticky top-0 z-30 border-b border-white/10 bg-[#07111F]/92 px-4 py-4 backdrop-blur-xl sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex min-w-0 items-center gap-3">
              <button
                type="button"
                onClick={() => setMobileOpen(true)}
                className="focus-ring inline-flex size-11 items-center justify-center rounded-xl border border-white/10 bg-white/[.06] text-white xl:hidden"
                aria-label="Open navigation"
              >
                <Menu className="size-5" aria-hidden="true" />
              </button>
              <div className="min-w-0">
                <p className="truncate text-xs font-black uppercase tracking-[0.18em] text-cyan-100">{contextLine}</p>
                <p className="mt-1 truncate text-sm font-semibold text-slate-300">{routeContext.ownerType} area</p>
              </div>
            </div>

            <div className="flex w-full flex-col gap-3 md:w-auto md:flex-row md:items-center">
              <label className="relative min-w-0 md:w-72">
                <span className="sr-only">Search Business Center OS</span>
                <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-slate-400" aria-hidden="true" />
                <input
                  type="search"
                  placeholder="Search clients, work, files..."
                  className="focus-ring min-h-11 w-full rounded-xl border border-white/10 bg-white/[.06] py-2 pl-10 pr-3 text-sm font-semibold text-white placeholder:text-slate-500"
                  aria-label="Search Business Center OS"
                />
              </label>
              <div className="flex items-center gap-2">
                <Link
                  to="/admin/automation-engine"
                  className="focus-ring inline-flex min-h-11 items-center justify-center rounded-xl border border-cyan-300/20 bg-cyan-300/10 px-4 text-sm font-black text-cyan-100"
                >
                  Quick Actions
                </Link>
                <button
                  type="button"
                  className="focus-ring inline-flex size-11 items-center justify-center rounded-xl border border-white/10 bg-white/[.06] text-slate-200"
                  aria-label="Notifications placeholder"
                >
                  <Bell className="size-5" aria-hidden="true" />
                </button>
                <div className="hidden min-h-11 items-center gap-3 rounded-xl border border-white/10 bg-white/[.06] px-3 md:flex">
                  <span className="grid size-8 place-items-center rounded-full bg-cyan-200 text-xs font-black text-slate-950">A</span>
                  <span className="text-sm font-black text-white">Admin</span>
                </div>
              </div>
            </div>
          </div>
        </header>

        <main id="business-center-os-content" className="min-h-screen" tabIndex={-1}>
          <section className="border-b border-white/10 px-4 py-8 sm:px-6 lg:px-8">
            <div className="mx-auto flex max-w-7xl flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
              <div className="min-w-0">
                <p className="text-xs font-black uppercase tracking-[0.18em] text-cyan-100">{eyebrow}</p>
                <h1 className="mt-2 max-w-5xl font-display text-4xl font-bold leading-tight text-white sm:text-5xl">{title}</h1>
                <p className="mt-4 max-w-4xl text-sm font-semibold leading-7 text-slate-300 sm:text-base">{description}</p>
              </div>
              {actions ? <div className="flex flex-wrap gap-3">{actions}</div> : null}
            </div>

            {statusItems.length ? (
              <div className="mx-auto mt-6 grid max-w-7xl gap-3 sm:grid-cols-2 xl:grid-cols-4">
                {statusItems.map((item) => (
                  <div key={item.label} className="rounded-2xl border border-white/10 bg-white/[.06] p-4">
                    <p className="text-xs font-black uppercase tracking-[0.14em] text-slate-400">{item.label}</p>
                    <p className="mt-2 font-display text-3xl font-bold text-white">{item.value}</p>
                  </div>
                ))}
              </div>
            ) : null}
          </section>

          <div className="px-4 py-10 sm:px-6 lg:px-8">
            <div className="mx-auto max-w-7xl">{children}</div>
          </div>
        </main>
      </div>
    </div>
  );
}

function SidebarContent({
  collapsed,
  onCollapseToggle,
  mobile = false,
}: {
  collapsed: boolean;
  onCollapseToggle: () => void;
  mobile?: boolean;
}) {
  const location = useLocation();

  return (
    <>
      <div className="flex items-center justify-between gap-3 border-b border-white/10 p-4">
        <Link to="/admin/business-center-os" className="focus-ring flex min-w-0 items-center gap-3 rounded-xl">
          <span className="grid size-11 shrink-0 place-items-center rounded-2xl bg-cyan-200 text-lg font-black text-slate-950">BC</span>
          {!collapsed ? (
            <span className="min-w-0">
              <span className="block truncate font-display text-xl font-bold text-white">Business Center OS</span>
              <span className="block truncate text-xs font-black uppercase tracking-[0.18em] text-cyan-100">Admin Shell</span>
            </span>
          ) : null}
        </Link>
        <button
          type="button"
          onClick={onCollapseToggle}
          className="focus-ring inline-flex size-10 shrink-0 items-center justify-center rounded-xl border border-white/10 bg-white/[.06] text-slate-200"
          aria-label={mobile ? "Close navigation" : collapsed ? "Expand sidebar" : "Collapse sidebar"}
          aria-expanded={!collapsed}
        >
          {mobile ? <X className="size-5" aria-hidden="true" /> : <ChevronLeft className={`size-5 transition ${collapsed ? "rotate-180" : ""}`} aria-hidden="true" />}
        </button>
      </div>

      <nav className="luxury-scrollbar flex-1 overflow-y-auto px-3 py-4" aria-label="Business Center OS sections">
        <div className="grid gap-5">
          {navGroups.map((group) => (
            <section key={group.title} aria-labelledby={`bc-nav-${slugify(group.title)}`}>
              {!collapsed ? (
                <h2 id={`bc-nav-${slugify(group.title)}`} className="px-3 text-[11px] font-black uppercase tracking-[0.18em] text-slate-500">
                  {group.title}
                </h2>
              ) : (
                <h2 id={`bc-nav-${slugify(group.title)}`} className="sr-only">
                  {group.title}
                </h2>
              )}
              <div className="mt-2 grid gap-1">
                {group.items.map((item) => (
                  <ShellNavLink key={`${group.title}-${item.to}`} item={item} pathname={location.pathname} collapsed={collapsed} />
                ))}
              </div>
            </section>
          ))}
        </div>
      </nav>

      <div className="border-t border-white/10 p-4">
        <div className="rounded-2xl border border-cyan-300/15 bg-cyan-300/10 p-4">
          {!collapsed ? (
            <>
              <p className="text-xs font-black uppercase tracking-[0.16em] text-cyan-100">Workspace Context</p>
              <p className="mt-2 text-sm font-semibold leading-6 text-slate-300">
                Navigation is grouped by operating area so every workspace can inherit the same admin spine.
              </p>
            </>
          ) : (
            <p className="text-center text-xs font-black text-cyan-100">OS</p>
          )}
        </div>
      </div>
    </>
  );
}

function ShellNavLink({ item, pathname, collapsed }: { item: ShellNavItem; pathname: string; collapsed: boolean }) {
  const routeOnly = item.to.split("#")[0];
  const isHashTarget = item.to.includes("#");
  const isActive =
    pathname === routeOnly ||
    (!isHashTarget && routeOnly !== "/admin/business-center-os" && pathname.startsWith(`${routeOnly}/`)) ||
    (routeOnly === "/admin/ar-recovery" && pathname.startsWith("/admin/ar-recovery"));

  return (
    <NavLink
      to={item.to}
      title={collapsed ? `${item.label}: ${item.description}` : undefined}
      className={({ isActive: navActive }) =>
        `focus-ring flex min-h-11 items-center gap-3 rounded-xl px-3 py-2 text-sm font-bold transition ${
          isActive || navActive
            ? "border border-cyan-300/30 bg-cyan-300/15 text-cyan-50"
            : "border border-transparent text-slate-300 hover:border-white/10 hover:bg-white/[.06] hover:text-white"
        } ${collapsed ? "justify-center" : ""}`
      }
    >
      <item.icon className="size-5 shrink-0" aria-hidden="true" />
      {!collapsed ? (
        <span className="min-w-0">
          <span className="block truncate">{item.label}</span>
          <span className="block truncate text-xs font-semibold text-slate-500">{item.description}</span>
        </span>
      ) : (
        <span className="sr-only">{item.label}</span>
      )}
    </NavLink>
  );
}

function slugify(value: string) {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
}
